import { EmployeeAvatarWithTooltip } from "@/components/employee/EmployeeAvatarWithTooltip.tsx";
import { CreatedByWorkflowInline } from "@/components/workflows/CreatedByWorkflowInline.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { CreationSourceTabs } from "@/components/filter/CreationSourceTabs.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useSurveysHook } from "@/hooks/use-surveys.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useSurveyCreate, useSurveySearch } from "@/queries/surveys/use-surveys.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCreationSource, ColumnSizes } from "@/types/common.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiSurveyFilter, ApiSurveySearchItem, ApiSurveyStatus } from "@/types/survey.types.ts";
import {
  Box,
  Button,
  ButtonLink,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  LinkTo,
  ListItem,
  Page,
  PaginationBlock,
  Stack,
  Table,
  TableCell,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce, useEffectOnce } from "react-use";
import { twMerge } from "tailwind-merge";

export const SurveysIndex = () => {
  const library = filtersLibrary();
  const { renderDate, formatPeriodDates } = useFormatter();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canIEdit } = usePermissions();
  const navigate = useNavigate();

  const {
    filter: { filter: filter },
  } = useAppFilter<ApiSurveyFilter>({
    filters: [library.page_number, library.text],
    source: "Surveys",
  });

  const rawSource = searchParams.get("source");
  const creationSource: ApiCreationSource | null =
    rawSource === "all"
      ? null
      : Object.values(ApiCreationSource).includes(rawSource as ApiCreationSource)
        ? (rawSource as ApiCreationSource)
        : ApiCreationSource.manual;

  const handleCreationSourceChange = (value: ApiCreationSource | undefined) => {
    setSearchParams((params) => {
      params.set("source", value ?? "all");
      params.delete("page");
      return params;
    });
  };

  const requestFilter = useMemo<ApiSurveyFilter>(
    () => ({ ...filter, creation_source: creationSource }),
    [filter, creationSource]
  );

  const { data: surveys, isLoading, isFetching } = useSurveySearch({ filter: requestFilter });
  const { t } = useTranslation();
  const createSurvey = useSurveyCreate();
  const [searchTerm, setSearchTerm] = useState("");

  const { getSurveyStatusTag, handleDeleteSurvey, isDeletePending } = useSurveysHook();

  useTitle(t("surveys:surveys"));

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set("search", searchTerm);
        else searchParams.delete("search");
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const columns = useMemo<ColumnDef<ApiSurveySearchItem>[]>(
    () => [
      {
        header: t("surveys:survey"),
        cell: (row) =>
          row.row.original.title ? (
            <LinkTo to={`/surveys/${row.row.original.id}/view/summary`} variant={"secondary"}>
              <ListItem
                emoji={row.row.original.emoji}
                title={row.row.original.title}
                forceShowAvatar={true}
                truncate={true}
                behavior={"card"}
              />
            </LinkTo>
          ) : (
            <ListItem
              emoji={undefined}
              preTitle={t("common:untitled")}
              forceShowAvatar={true}
              truncate={true}
              behavior={"card"}
            />
          ),
        accessorKey: "title",
        meta: { disableRowClick: true },
        ...ColumnSizes.employee,
      },
      {
        header: t("common:owner"),
        cell: (row) =>
          row.row.original.created_by_workflow ? (
            <CreatedByWorkflowInline
              workflow={row.row.original.created_by_workflow}
              author={row.row.original.owners?.[0]}
            />
          ) : (
            <EmployeeAvatarWithTooltip size={"sm"} employees={row.row.original.owners} enableLink />
          ),
        accessorKey: "owner",
        meta: { disableRowClick: true },
        ...ColumnSizes.narrow,
      },
      {
        header: t("common:status"),
        cell: (row) => <Stack items={"start"}>{getSurveyStatusTag(row.row.original.status)}</Stack>,
        accessorKey: "status",
        ...ColumnSizes.status,
      },
      {
        header: t("common:period"),
        cell: (row) => {
          if (row.row.original.date_starts_at && row.row.original.date_ends_at) {
            return (
              <TableCell variant={"secondary"}>
                {
                  formatPeriodDates({
                    dateStart: row.row.original.date_starts_at,
                    dateEnd: row.row.original.date_ends_at,
                  })?.title
                }
              </TableCell>
            );
          } else if (row.row.original.date_starts_at) {
            return (
              <TableCell variant={"secondary"}>
                {renderDate(row.row.original.date_starts_at)} -
              </TableCell>
            );
          } else if (row.row.original.date_ends_at) {
            return (
              <TableCell variant={"secondary"}>
                - {renderDate(row.row.original.date_ends_at)}
              </TableCell>
            );
          }

          return <TableCell variant={"secondary"}>-</TableCell>;
        },
        accessorKey: "start_date",
        ...ColumnSizes.status,
      },

      {
        header: t("common:progress"),
        cell: (cell) => (
          <Tooltip>
            <TooltipTrigger>
              <Box className="w-full py-1 pr-6">
                <Box
                  className={twMerge(
                    "h-1 rounded-xs bg-dark/10 w-full px-2 overflow-hidden relative"
                  )}
                >
                  <Box
                    style={{
                      width:
                        cell.row.original.participants_stats.total_count == 0
                          ? 0
                          : (cell.row.original.participants_stats.completed_count /
                              cell.row.original.participants_stats.total_count) *
                              100 +
                            "%",
                    }}
                    className={twMerge("absolute left-0 h-full rounded-xs bg-brand-dark")}
                  ></Box>
                </Box>
              </Box>
            </TooltipTrigger>
            <TooltipContent>
              {t("performance_review:reviews_completed_of", {
                current: cell.row.original.participants_stats.completed_count,
                total: cell.row.original.participants_stats.total_count,
              })}
            </TooltipContent>
          </Tooltip>
        ),
        accessorKey: "participants_stats",
        ...ColumnSizes.middle,
      },
      {
        header: t("common:participants"),
        cell: (cell) =>
          cell.row.original.acl.can_edit &&
          [ApiSurveyStatus.draft].includes(cell.row.original.status) ? (
            <ButtonLink
              href={`/surveys/${cell.row.original.id}/setup/details`}
              variant={"secondary"}
            >
              {t("common:setup")}
            </ButtonLink>
          ) : (
            <EmployeeAvatarWithTooltip
              size="sm"
              employees={cell.row.original.participants_preview}
              maxVisible={3}
              totalCount={cell.row.original.participants_stats.total_count}
              enableLink
            />
          ),
        accessorKey: "participants_preview",
        meta: { disableRowClick: true },
        ...ColumnSizes.middle,
      },
    ],
    [isDeletePending]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiSurveySearchItem>) => {
      if (!row.original.acl.can_edit) return null;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />}
            to={`/surveys/${row.original.id}/setup/details`}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
            variant={"menuDanger"}
            disabled={isDeletePending}
            onClick={() => handleDeleteSurvey(row.original)}
            title={`${t("common:delete")}...`}
          />
        </DropdownMenu>
      );
    },
    [handleDeleteSurvey, isDeletePending, t]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.surveys.viewed);
  });

  const handleSurveyCreate = async () => {
    try {
      const data = await createSurvey.mutateAsync();
      analyticsService.trackEvent(analyticEvents.surveys.created, {
        [analyticProperties.id]: data.id,
      });
      navigate(`/surveys/${data.id}/setup/details`);
    } catch {
      // todo add log
    }
  };

  return (
    <>
      <Page className="h-full">
        <Page.Header title={t("surveys:surveys")}>
          {canIEdit(ApiPermission.surveys_create) && (
            <Button isLoading={createSurvey.isPending} onClick={handleSurveyCreate}>
              {t("surveys:create_survey")}
            </Button>
          )}
        </Page.Header>

        <Page.Content stretch>
          <Page.StickyLayout
            toolbar={
              <>
                <CreationSourceTabs
                  value={creationSource ?? undefined}
                  onChange={handleCreationSourceChange}
                  sources={[ApiCreationSource.manual, ApiCreationSource.workflow, undefined]}
                />
                <Box className={"mt-2"}>
                  <Search
                    value={searchTerm}
                    onInput={(e) => {
                      setSearchTerm(e.currentTarget.value);
                    }}
                    type={"text"}
                    placeholder={t("common:search_by_name")}
                    className={"w-96"}
                  />
                </Box>
              </>
            }
            footer={
              <PaginationBlock
                metadata={surveys?.metadata}
                onPageChange={(page) => {
                  setSearchParams((searchParams) => {
                    searchParams.set("page", page.toString());
                    return searchParams;
                  });
                }}
              />
            }
          >
            <Table
              stickyHeader={true}
              onRowClick={(row) => navigate(`/surveys/${row.original.id}/view/summary`)}
              renderRowAction={renderRowAction}
              data={surveys?.items || []}
              columns={columns}
              showSkeleton={isLoading}
              isFetching={isLoading || isFetching || isDeletePending}
              showNotFoundError={
                (filter.text != undefined || creationSource === ApiCreationSource.workflow) &&
                surveys?.metadata.total_items == 0
              }
              notFoundComponent={<EmptyStateNothingFound />}
              emptyStateComponent={
                <EmptyStateBase
                  emoji={":dog:"}
                  title={t("surveys:surveys_empty_state_title")}
                  description={t("surveys:surveys_empty_state_description")}
                  primaryButton={
                    canIEdit(ApiPermission.surveys_create) && (
                      <Button isLoading={createSurvey.isPending} onClick={handleSurveyCreate}>
                        {t("surveys:create_survey")}
                      </Button>
                    )
                  }
                />
              }
            />
          </Page.StickyLayout>
        </Page.Content>
      </Page>
    </>
  );
};
